/* eslint-disable @typescript-eslint/no-unused-vars */

import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './OptionItem.module.scss';

export enum OptionItemAccessory {
  Icon = 'icon',
  Thumbnail = 'thumbnail',
  BoxIcon = 'box_icon',
}

export enum OptionItemTone {
  Default = 'default',
  Destructive = 'destructive',
}

export type OptionItemProps = {
  id?: string | number | null;
  label: string;
  link?: string;
  tone?: OptionItemTone;
  accessory?: OptionItemAccessory;
  accessoryItem?: string;
  shortcutText?: string;
  subtitle?: string;
  increasedSize?: boolean;
  disabled?: boolean;
  isError?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  isIcon?: boolean;
  accessoryBg?: string;
};

const OptionItem: React.FC<OptionItemProps> = ({
  label,
  link,
  tone = OptionItemTone.Default,
  accessory = OptionItemAccessory.Icon,
  accessoryItem,
  shortcutText,
  subtitle,
  increasedSize = false,
  disabled = false,
  isError = false,
  onClick,
  id = null,
  isIcon = false,
  accessoryBg,
}) => {
  const { t } = useTranslation();
  const hasAccessory = accessoryItem && accessory;
  const isBoxIcon = accessory === OptionItemAccessory.BoxIcon;
  const isThumbnail = accessory === OptionItemAccessory.Thumbnail;
  const isToneDestructive = tone === OptionItemTone.Destructive || isError;

  const body = (
    <>
      <div className="flex items-start flex-1">
        {accessoryItem && accessory && (
          <div
            style={accessoryBg ? { background: accessoryBg } : undefined}
            className={classNames({
              '!bg-primary': isIcon && !accessoryBg,
              [styles.boxIcon]: isBoxIcon,
              [styles.thumbnail]: isThumbnail,
              'opacity-60': !isThumbnail && !isBoxIcon && !isToneDestructive,
            })}>
            <Icon
              height={20}
              width={20}
              name={accessoryItem as IconName}
              className={classNames(
                { 'text-disabled': disabled, 'text-white': isIcon },
                `${isToneDestructive && 'error'}`,
              )}
            />
          </div>
        )}
        <div className={classNames('flex flex-col justify-start', `typo-text-14 ${hasAccessory ? 'pl-8' : ''}`)}>
          <span
            className={classNames(
              styles.title,
              { 'text-disabled': disabled },
              { 'status-error-full': isToneDestructive },
              { 'text-full': !disabled && !isToneDestructive && tone === OptionItemTone.Default },
            )}>
            {t(label)}
          </span>
          {subtitle && <span className="text-faded typo-text-12 mt-1">{subtitle}</span>}
        </div>
      </div>
      {shortcutText && <span className="text-faded typo-text-14 ml-auto">{shortcutText}</span>}
    </>
  );

  const location = useLocation();
  const isQueryActive = link
    ? location.pathname === '/settings' &&
      location.search === new URLSearchParams(link.split('?')[1]).toString().replace(/^/, '?')
    : false;

  const mainClasses = classNames(`${styles.root} rounded-base bg-transparent hover:surface-elevated-darken`, {
    'p-16': increasedSize,
    'px-12 py-8': !increasedSize,
    'pointer-events-none': disabled,
    [styles.active]: isQueryActive,
    'surface-elevated-darken': isQueryActive,
  });

  return link ? (
    <NavLink aria-disabled={disabled} to={link} onClick={onClick} className={mainClasses}>
      {body}
    </NavLink>
  ) : (
    <button type="button" onClick={onClick} className={mainClasses}>
      {body}
    </button>
  );
};

export default OptionItem;
